import React, { useMemo, useState } from "react";
import { FormikProvider, useFormik, FormikErrors } from "formik";
import { formatISO, isValid, parse } from "date-fns";

import { fullDateLongFormat } from "@common/helpers/date";
import { pluralize } from "@common/helpers/string";
import { FinancialAccountType } from "@common/types/financialAccount";
import { useFinancialAccounts } from "@hooks/useFinancialAccounts";
import { useEntitiesById } from "@hooks/useEntities";
import { CategoryStructure, useCategories } from "@hooks/useCategories";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { submitHelper } from "@helpers/form";
import { StandardModal } from "@design-system/Modal/Modal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { AddTransactionsModalContent } from "./AddTransactionsModalContent";
import { useTransactionsCreation } from "./useTransactionsCreationMutation";
import { AddTransactionModalMutableConfig } from "./AddTransactionsModal.lib";

export interface TransactionProxyObject {
    id: string;
    date?: string;
    description: string;
    amount?: number;
    category: string;
    accountId?: string | null;
    classIds?: string[];
}

interface Props {
    show: boolean;
    onHide: () => void;
}

export interface AddTransactionsModalFormikConfig {
    isHideAfterSubmit: boolean;
    entityId?: number;
    financialAccountId?: number;
    transactions: TransactionProxyObject[];
}

export type AddTransactionsModalFormErrors =
    FormikErrors<AddTransactionsModalFormikConfig> & {
        noTransactions?: string;
    };

const DEFAULT_NUMBER_OF_LINES = 10;

function buildEmptyLines(
    size: number = DEFAULT_NUMBER_OF_LINES,
): TransactionProxyObject[] {
    return new Array(size).fill(null).map(() => ({
        id: Math.random().toString(),
        date: undefined,
        category: "",
        accountId: undefined,
        amount: undefined,
        description: "",
        classIds: undefined,
    }));
}

function parseRowDate(value?: string): Date | undefined {
    if (!value) {
        return undefined;
    }
    const date = parse(value, fullDateLongFormat, new Date());
    return isValid(date) ? date : undefined;
}

function getRowDateError(value?: string): string | undefined {
    if (!value) {
        return "Missing date";
    }
    return parseRowDate(value) ? undefined : "Invalid date";
}

const hasAmountAndDate = ({
    amount,
    date: transactionDate,
}: TransactionProxyObject): boolean =>
    !!amount && amount !== 0 && !!parseRowDate(transactionDate);

const isFilledCategoryRow = (row: TransactionProxyObject): boolean =>
    hasAmountAndDate(row) && !!row.category;

export const AddTransactionsModal: React.FC<Props> = React.memo(
    ({ show, onHide }) => {
        const [loading, setLoading] = useState(false);
        const { toast } = useToaster();
        const [submittedWithErrors, setSubmittedWithErrors] = useState(false);
        const entitiesById = useEntitiesById();

        const save = useTransactionsCreation();
        const { enabled: isGlFirst } = useIsGlFirst();

        const categories = useCategories({ structure: CategoryStructure.FLAT });
        const bankAccounts = useFinancialAccounts({
            createTempCashAccounts: true,
        });

        const validate = useMemo(
            () => (values: AddTransactionsModalFormikConfig) => {
                if (
                    !AddTransactionModalMutableConfig.validate &&
                    !submittedWithErrors
                ) {
                    return { transactions: undefined };
                }

                if (!values.financialAccountId) {
                    if (AddTransactionModalMutableConfig.validate) {
                        setSubmittedWithErrors(true);
                        AddTransactionModalMutableConfig.validate = false;
                    }
                    return {
                        financialAccountId:
                            "Financial Account must be selected",
                    };
                }

                const isFilledRow = isGlFirst
                    ? hasAmountAndDate
                    : isFilledCategoryRow;

                const hasAtLeastOneFilledTransaction =
                    values.transactions.some(isFilledRow);

                if (!hasAtLeastOneFilledTransaction) {
                    if (AddTransactionModalMutableConfig.validate) {
                        setSubmittedWithErrors(true);
                        AddTransactionModalMutableConfig.validate = false;
                    }
                    return {
                        noTransactions: "Add at least one transaction",
                    };
                }

                let hasTransactionError = false;
                const transactionErrors: Array<
                    | {
                          amount?: string;
                          date?: string;
                          category?: string;
                      }
                    | undefined
                > = [];

                const selectedAccount = bankAccounts.find(
                    ({ id }) => id === values.financialAccountId,
                );

                const categoryById = new Map(
                    categories.map((cat) => [cat.id, cat]),
                );

                values.transactions.forEach((transaction) => {
                    if (isGlFirst) {
                        if (hasAmountAndDate(transaction)) {
                            transactionErrors.push({});
                        } else if (
                            !transaction.amount &&
                            !transaction.date &&
                            !transaction.accountId
                        ) {
                            transactionErrors.push({});
                        } else {
                            hasTransactionError = true;
                            transactionErrors.push({
                                amount: !transaction.amount
                                    ? "Missing amount"
                                    : undefined,
                                date: getRowDateError(transaction.date),
                            });
                        }
                        return;
                    }

                    if (isFilledCategoryRow(transaction)) {
                        if (
                            selectedAccount?.entity?.isBusiness !==
                            categoryById.get(transaction.category)?.isBusiness
                        ) {
                            hasTransactionError = true;
                            transactionErrors.push({
                                category:
                                    "Assign a category matching the account type (personal or business)",
                            });
                        } else {
                            transactionErrors.push({});
                        }
                    } else if (
                        !transaction.amount &&
                        !transaction.date &&
                        !transaction.category
                    ) {
                        transactionErrors.push({});
                    } else {
                        hasTransactionError = true;
                        transactionErrors.push({
                            amount: !transaction.amount
                                ? "Missing amount"
                                : undefined,
                            date: getRowDateError(transaction.date),
                            category: !transaction.category
                                ? "Missing category"
                                : undefined,
                        });
                    }
                });

                if (hasTransactionError) {
                    if (AddTransactionModalMutableConfig.validate) {
                        setSubmittedWithErrors(true);
                        AddTransactionModalMutableConfig.validate = false;
                    }
                    return { transactions: transactionErrors };
                }

                return undefined;
            },
            [submittedWithErrors, bankAccounts, categories, isGlFirst],
        );

        const formik = useFormik<AddTransactionsModalFormikConfig>({
            validateOnBlur: true,
            validateOnChange: true,
            initialValues: {
                transactions: buildEmptyLines(),
                isHideAfterSubmit: true,
            },
            enableReinitialize: false,
            validate,
            onSubmit: submitHelper({
                loading,
                setLoading,
                handler: async (
                    {
                        transactions,
                        financialAccountId,
                        isHideAfterSubmit,
                        entityId,
                    },
                    { validateForm, resetForm },
                ) => {
                    const errors = await validateForm({
                        transactions,
                        financialAccountId,
                    });

                    if (Object.keys(errors).length > 0) {
                        setSubmittedWithErrors(true);
                        return;
                    }

                    const transactionsToCreate = transactions
                        .filter(
                            isGlFirst ? hasAmountAndDate : isFilledCategoryRow,
                        )
                        .map((transaction) => ({
                            ...(isGlFirst
                                ? {
                                      accountId:
                                          transaction.accountId ?? undefined,
                                  }
                                : { categoryId: transaction.category }),
                            dateOnly: formatISO(
                                parseRowDate(transaction.date) ?? new Date(),
                                { representation: "date" },
                            ),
                            amount: transaction.amount ?? 0,
                            memo: transaction.description,
                            classIds: transaction.classIds,
                        }));

                    const result = await save.mutateAsync({
                        transactions: transactionsToCreate,
                        entityId: entityId!,
                        ...(financialAccountId && financialAccountId > 0
                            ? { financialAccountId }
                            : { createCashAccount: true }),
                    });

                    if (result) {
                        const isNotCash =
                            bankAccounts.find(
                                ({ id }) => id === financialAccountId,
                            )?.financialType !== FinancialAccountType.CASH;

                        const toastContent =
                            financialAccountId &&
                            financialAccountId > 0 &&
                            isNotCash
                                ? `Added ${transactionsToCreate.length} ${pluralize("transaction", transactionsToCreate.length)} to account ${bankAccounts.find(({ id }) => id === financialAccountId)?.name}`
                                : `Added ${transactionsToCreate.length} cash ${pluralize("transaction", transactionsToCreate.length)} to entity ${entityId !== undefined ? entitiesById.get(entityId)?.name : ""}`.trimEnd();
                        toast(toastContent);
                    }

                    resetForm();
                    if (isHideAfterSubmit) {
                        onHide();
                    }
                    setSubmittedWithErrors(false);
                },
            }),
        });

        return (
            <FormikProvider value={formik}>
                <StandardModal
                    size="xl"
                    onHide={() => {
                        onHide();
                        formik.resetForm();
                    }}
                    show={show}
                    disableAnimation
                >
                    <AddTransactionsModalContent loading={loading} />
                </StandardModal>
            </FormikProvider>
        );
    },
);
